import { useEffect, useRef, useState } from 'react';
import api from '../api';
import VehicleContractAgreement from './VehicleContractAgreement';
import '../management-module.css';
import '../business-analytics.css';

const id = value => String(value?._id || value || '');
const money = value => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function VehicleContractPage() {
  const [vehicles, setVehicles] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const fileInput = useRef(null);

  useEffect(() => {
    let current = true;
    api.get('/management/vehicle-contracts')
      .then(response => { if (current) { setVehicles(response.data.vehicles || []); setContracts(response.data.data || []); } })
      .catch(err => { if (current) setError(err.response?.data?.message || 'Unable to load vehicle contract agreements.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, []);

  const saveContract = saved => setContracts(current => current.some(contract => id(contract.vehicle) === id(saved.vehicle))
    ? current.map(contract => id(contract.vehicle) === id(saved.vehicle) ? saved : contract)
    : [...current, saved].sort((left, right) => String(left.vehicleNumber).localeCompare(String(right.vehicleNumber), 'en', { numeric: true })));

  const importFile = async file => {
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) { setError('Please select an .xlsx Excel file.'); return; }
    setImporting(true); setError(''); setImportMessage('');
    try {
      const formData = new FormData(); formData.append('file', file);
      const response = await api.post('/management/vehicle-contracts/import', formData);
      setContracts(current => {
        const imported = response.data.data || [];
        const merged = new Map(current.map(contract => [id(contract.vehicle), contract]));
        imported.forEach(contract => merged.set(id(contract.vehicle), contract));
        return [...merged.values()].sort((left, right) => String(left.vehicleNumber).localeCompare(String(right.vehicleNumber), 'en', { numeric: true }));
      });
      setImportMessage(response.data.message);
    } catch (err) { setError(err.response?.data?.message || 'Unable to import the agreement file.'); }
    finally { setImporting(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const updateStatus = async contract => {
    const vehicleId = id(contract.vehicle);
    const currentStatus = contract.status || 'active';
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`${nextStatus === 'inactive' ? 'Mark' : 'Activate'} the agreement for ${contract.vehicleNumber}?`)) return;
    setStatusUpdatingId(vehicleId); setError(''); setImportMessage('');
    try {
      const response = await api.patch(`/management/vehicle-contracts/${vehicleId}/status`, { status: nextStatus });
      setContracts(current => current.map(item => id(item.vehicle) === vehicleId ? response.data : item));
      setImportMessage(`Client agreement marked ${nextStatus}.`);
    } catch (err) { setError(err.response?.data?.message || 'Unable to update the client agreement status.'); }
    finally { setStatusUpdatingId(''); }
  };

  return <div className="management-module ba-workspace ba-contract-page">
    <div className="ba-contract-page-heading">
      <div><p>FLEET MANAGEMENT</p><h2>Client Agreement</h2><span>Select a vehicle and save its fixed rate, included KM and extra KM charge.</span></div>
      <div className="ba-contract-page-actions"><div className="ba-contract-import" onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); importFile(event.dataTransfer.files[0]); }}><input ref={fileInput} type="file" accept=".xlsx" onChange={event => importFile(event.target.files[0])} /><button type="button" onClick={() => fileInput.current?.click()} disabled={importing}>{importing ? 'Importing…' : 'Drop Excel or Upload'}</button><small>Client, Client Location, Vehicle Number, Fixed Rate, Fixed KM, Extra KM Rate, Contract Days (optional)</small></div><button type="button" onClick={() => { setEditingContract(null); setFormOpen(true); }}>Create Agreement</button><span>{contracts.length} saved {contracts.length === 1 ? 'agreement' : 'agreements'}</span></div>
    </div>
    {error && <p className="management-error" role="alert">{error}</p>}
    {importMessage && <p className="ba-contract-success" role="status">{importMessage}</p>}
    {loading ? <p className="ba-loading" role="status">Loading vehicle contracts…</p> : <>
      <VehicleContractAgreement open={formOpen} initialVehicleId={editingContract ? id(editingContract.vehicle) : ''} vehicles={vehicles} contracts={contracts} onClose={() => { setFormOpen(false); setEditingContract(null); }} onSaved={saveContract} />
      <section className="ba-contract-list" aria-labelledby="saved-contracts-title">
        <div><h3 id="saved-contracts-title">Client Agreements</h3><span>{vehicles.length} vehicles available</span></div>
        {contracts.length ? (
          <div className="ba-performance-table">
            <table>
              <thead>
                <tr>
                  <th>Client</th><th>Client Location</th><th>Vehicle Number</th><th>Contract Type</th><th>Fixed Rate</th><th>Contract Days</th><th>Fixed KM</th><th>Extra KM Rate</th><th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map(contract => {
                  const status = contract.status || 'active';
                  return <tr key={contract._id || id(contract.vehicle)}>
                    <td>{contract.clientName || 'Unassigned'}</td>
                    <td>{contract.clientLocationName || 'Unassigned'}</td>
                    <td><strong>{contract.vehicleNumber}</strong></td>
                    <td>{contract.contractType === 'rental' ? 'Rental' : 'Monthly Contract'}</td>
                    <td>{money(contract.fixedRate)}</td>
                    <td>{contract.contractDays == null ? 'Not set' : Number(contract.contractDays).toLocaleString('en-IN')}</td>
                    <td>{Number(contract.fixedKm).toLocaleString('en-IN')}</td>
                    <td>{money(contract.extraKmRate)} / KM</td>
                    <td><span className={`ba-contract-status ${status}`}>{status}</span></td>
                    <td><div className="ba-contract-row-actions"><button className="ba-contract-edit" type="button" onClick={() => { setEditingContract(contract); setFormOpen(true); }}>Edit</button><button className={`ba-contract-toggle ${status}`} type="button" onClick={() => updateStatus(contract)} disabled={statusUpdatingId === id(contract.vehicle)}>{statusUpdatingId === id(contract.vehicle) ? 'Saving…' : status === 'active' ? 'Active' : 'Inactive'}</button></div></td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="ba-contract-empty">No client agreements have been saved yet.</p>}
      </section>
    </>}
  </div>;
}
